import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ClipboardList, Users, Settings, LogOut, Aperture, CalendarDays, UserCog, Menu, Plug, Music, FileText, Timer, ListChecks, Workflow } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ThemeToggle from "@/components/ThemeToggle";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", color: "#818cf8" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar", color: "#a78bfa" },
  { to: "/orders", label: "Orders", icon: ClipboardList, testid: "nav-orders", color: "#c084fc" },
  { to: "/workload", label: "Workload", icon: UserCog, testid: "nav-workload", color: "#e0a3f0" },
  { to: "/time-tracking", label: "Time Tracking", icon: Timer, testid: "nav-time-tracking", color: "#f0a6c0" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", color: "#f4a3b8" },
  { to: "/automations", label: "Automations", icon: Workflow, testid: "nav-automations", color: "#7dd3fc" },
  { to: "/music", label: "Music", icon: Music, testid: "nav-music", color: "#fb9db0" },
  { to: "/templates", label: "Templates", icon: FileText, testid: "nav-templates", color: "#fbc98a" },
  { to: "/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations", color: "#6ee7b7" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings", color: "#67e8f9" },
];

function SidebarInner({ user, onLogout, onNavigate }) {
  const allow = (to) => {
    if (!user?.is_member) return true;
    const page = to.replace("/", "");
    if (page === "my-tasks") return true;
    if (page === "integrations") return false;
    return !!(user.permissions || {})[page];
  };
  const nav = (user?.is_member
    ? [{ to: "/my-tasks", label: "My Tasks", icon: ListChecks, testid: "nav-my-tasks", color: "#818cf8" }, ...NAV]
    : NAV).filter((i) => allow(i.to));
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-6 py-6">
        <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-primary/15 ring-1 ring-primary/30">
          {user?.logo_url ? <img src={user.logo_url} alt="logo" className={`h-full w-full object-contain ${user?.logo_invert !== false ? "dark:invert" : ""}`} data-testid="brand-logo" />
            : <Aperture className="h-5 w-5 text-primary" />}
        </div>
        <div>
          <div className="font-heading text-base font-extrabold leading-none tracking-tight" data-testid="brand-name">{user?.business_name || "HomePix Ops"}</div>
          <div className="overline text-muted-foreground mt-1">Media Workflow</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <item.icon className="h-4 w-4" style={{ color: item.color }} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border p-3">
        <div className="mb-2 px-1">
          <ThemeToggle />
        </div>
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <Avatar className="h-8 w-8">
            {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
            <AvatarFallback className="bg-secondary text-xs">
              {(user?.name || "U").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{user?.name}</div>
            <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive"
            title="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-col border-r border-border bg-card/40 md:flex" data-testid="sidebar">
        <SidebarInner user={user} onLogout={handleLogout} />
      </aside>

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar: hamburger (mobile) + global search (all sizes) */}
        <header className="relative z-50 flex items-center gap-3 border-b border-border bg-card/60 px-4 py-2.5 backdrop-blur" data-testid="top-bar">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
                className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-border bg-card p-0" data-testid="mobile-drawer">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">Main navigation links for HomePix Ops</SheetDescription>
              <SidebarInner user={user} onLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <GlobalSearch />
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="print-brand hidden items-center gap-3">
            {user?.logo_url && <img src={user.logo_url} alt="logo" className={user?.logo_invert !== false ? "dark:invert" : ""} />}
            <span className="font-heading text-lg font-extrabold tracking-tight">{user?.business_name || "HomePix Ops"}</span>
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}
